<template>
    <div class="tw:flex tw:flex-col tw:gap-6">

        <!-- Resumen del abonado -->
        <InvitacionesAbonadoResumen
            :abono-id="abonoId"
            :nombre="nombreAbonado"
            :zona="zonaAbonado"
            :fila="filaAbonado"
            :asiento="asientoAbonado"
        />

        <!-- Stats de invitaciones (con skeleton propio) -->
        <InvitacionesStats
            :stats="stats || {}"
            :loading="loadingStats"
            @ver-historial="openConsumidasModal"
        />

        <!-- Lista de eventos disponibles (con skeleton propio) -->
        <InvitacionesEventosList
            v-if="invitacionesHabilitadas && activo && activoTemporadaActual && !hasDashboardError"
            :eventos="eventos"
            :loading="loadingEventos"
            :error="errorEventos"
            @generar="onGenerar"
        />

        <!-- Mensaje cuando no hay eventos disponibles por condiciones -->
        <div
            v-else-if="!hasDashboardError"
            class="tw:bg-slate-50 tw:border tw:border-slate-200 tw:text-slate-600 tw:px-4 tw:py-3 tw:rounded-lg tw:text-center"
        >
            <p class="tw:m-0 tw:text-sm">{{ $t('perfil_invitaciones.sin_eventos_disponibles') }}</p>
        </div>

        <!-- Modal historial consumidas -->
        <InvitacionesConsumidasModal
            :show="showConsumidasModal"
            :items="consumidas"
            :loading="loadingConsumidasModal"
            :error="errorConsumidas"
            :page="consumidasMeta.current_page"
            :per-page="consumidasMeta.per_page"
            :total="consumidasMeta.total"
            @close="closeConsumidasModal"
            @page-change="fetchConsumidasPagina"
            @per-page-change="onPerPageChange"
        />
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import store from '@/store';
    import { useInvitaciones } from '@/composables/front/perfil/useInvitaciones';
    import InvitacionesAbonadoResumen from '../components/invitaciones/InvitacionesAbonadoResumen.vue';
    import InvitacionesStats from '../components/invitaciones/InvitacionesStats.vue';
    import InvitacionesEventosList from '../components/invitaciones/InvitacionesEventosList.vue';
    import InvitacionesConsumidasModal from '../components/invitaciones/InvitacionesConsumidasModal.vue';

    const props = defineProps({
        /**
         * ID del abonado cuyo dashboard de invitaciones se muestra.
         * Se pasa desde PerfilV2 cuando el usuario activa el flujo inline.
         */
        abonoId: {
            type: [String, Number],
            required: true,
        },
        /**
         * Nombre del abonado para el bloque de resumen.
         * Viene del abono seleccionado en AbonosList. Puede ser null si el
         * payload del listado no lo expone.
         */
        nombreAbonado: {
            type: String,
            default: null,
        },
        /**
         * Zona del asiento del abonado (p.e. "Tribuna Preferente").
         * Campo opcional — puede no estar disponible en el payload del listado.
         */
        zonaAbonado: {
            type: String,
            default: null,
        },
        /**
         * Fila del asiento del abonado.
         * Campo opcional — puede no estar disponible en el payload del listado.
         */
        filaAbonado: {
            type: [String, Number],
            default: null,
        },
        /**
         * Número de asiento del abonado.
         * Campo opcional — puede no estar disponible en el payload del listado.
         */
        asientoAbonado: {
            type: [String, Number],
            default: null,
        },
        /**
         * Indica si el abono está activo.
         * Si es false, se bloquea el contenido del dashboard.
         */
        activo: {
            type: Boolean,
            default: false,
        },
        /**
         * Indica si el abono está activo en la temporada actual (renovado).
         */
        activoTemporadaActual: {
            type: Boolean,
            default: false,
        },
    });

    /**
     * Emitido cuando el usuario pulsa "Generar invitaciones" en un evento.
     * El payload es el objeto evento completo. PerfilV2 lo recibe para
     * iniciar la transición al stepper (fase 4) cuando esté implementado.
     */
    const emit = defineEmits(['generar']);

    const teatro = computed(() => store.getters.getTeatro);
    const invitacionesHabilitadas = computed(() => Boolean(teatro.value?.invitaciones_para_abonados));

    const {
        // State — stats
        stats,
        loadingStats,

        // State — eventos
        eventos,
        loadingEventos,
        errorEventos,

        // State — consumidas (modal)
        consumidas,
        loadingConsumidasModal,
        errorConsumidas,
        consumidasMeta,
        showConsumidasModal,

        // Computed guards
        hasDashboardError,
        hasInvitacionesDisponibles,

        // Métodos modal
        openConsumidasModal,
        closeConsumidasModal,
        fetchConsumidasPagina,
    } = useInvitaciones(() => props.abonoId);

    /**
     * El usuario pulsó "Generar" en un evento del listado.
     * Se emite el evento al padre con el payload enriquecido con statsRestantes,
     * para que PerfilV2 pueda calcular el maxAsientos del stepper.
     *
     * @param {Object} evento  Objeto evento del listado
     */
    const onGenerar = evento => {
        emit('generar', {
            evento,
            statsRestantes: stats.value ? stats.value.restantes ?? null : null,
        });
    };

    /**
     * El usuario cambió el número de items por página en el modal de consumidas.
     * PaginationFooterFront emite { page, perPage }; reseteamos a la página 1
     * con el nuevo tamaño de página.
     *
     * @param {{ page: number, perPage: number }} payload
     */
    const onPerPageChange = ({ page, perPage }) => {
        consumidasMeta.value = { ...consumidasMeta.value, per_page: perPage };
        fetchConsumidasPagina(page);
    };
</script>
